import type { ComponentPropsWithRef, ReactNode, Ref } from 'react';
import { Loader2 } from 'lucide-react';

import { cn } from '@rocco/utils/cn';
import type { EnquirerType, EnquiryProjectKind } from 'generated/graphql';
import {
    GATE_FIELD_INPUT_CLASS,
    GateFieldError,
    GateFieldLabel,
    GateInput,
    GateTextarea,
} from '~/modules-rocco/user/component/auth-modal/GateFormControls';
import { GateStickyFooter } from '~/modules-rocco/user/component/auth-modal/GateStickyFooter';
import {
    ENQUIRER_TYPE_OPTIONS,
    FIELD_QUESTIONS,
    PROJECT_KIND_OPTIONS,
} from '~/modules/business-manager/forms/types';
import type {
    ConfiguredEnquiryAnswers,
    ConfiguredEnquiryErrors,
    ConfiguredEnquiryRules,
} from '~/modules/enquiry/domain/configured-enquiry';
import { EnquiryGateCustomFields } from './EnquiryGateCustomFields';
import { GateSelectField } from './GateSelectField';

/**
 * The enquiry gate's form, as presentational pieces. The live gate wires them
 * to `useEnquiry` and react-hook-form; the Business Manager preview wires them
 * to local state. Both render these, so the preview is the gate a visitor sees.
 */

/** The message box hint when neither the form nor the page words one. */
export const ENQUIRY_GATE_MESSAGE_HINT =
    'E.g. Hi there, we are planning a residential new build in Auckland and are interested in working with you. Please let us know if you’re available for a chat.';

/** The submit wording when the form does not choose its own. */
export const ENQUIRY_GATE_SUBMIT_LABEL = 'Continue';

/**
 * The professional's own words above the form. They ride in the brand pane on
 * the desktop card; a surface without that pane shows them here instead, or a
 * supplier's "email us instead" note is simply absent.
 */
export const EnquiryGateNote = ({ children }: { children: ReactNode }) => (
    <p className="mb-4 text-[0.9375rem] leading-snug whitespace-pre-wrap text-[#656565]">
        {children}
    </p>
);

type GateInputProps = ComponentPropsWithRef<typeof GateInput>;
type GateTextareaProps = ComponentPropsWithRef<typeof GateTextarea>;

const INPUT_CLASS = cn(GATE_FIELD_INPUT_CLASS, 'w-full');

export interface EnquiryGateContactFieldsProps {
    firstName: string;
    lastName: string;
    onNameChange: (firstName: string, lastName: string) => void;
    onNameBlur?: () => void;
    /** Focus target for a `name` error. */
    firstNameRef?: Ref<HTMLInputElement>;
    /** Value, change and blur wiring for the email input (e.g. a register result). */
    emailInput: GateInputProps;
    /** Value, change and blur wiring for the phone input. */
    phoneInput: GateInputProps;
    /** The address is the account's own and cannot be changed here. */
    emailLocked?: boolean;
    errors: { name?: string; email?: string; phoneNumber?: string };
    disabled?: boolean;
    /**
     * Email and phone side by side. Only where the pane is wide enough: at half
     * of a 414px sheet an address truncates mid-domain, which is the part the
     * reader needs to check before sending. Names always pair; they are short.
     */
    twoColumns: boolean;
}

/** Name, email and phone: the contact details every enquiry carries. */
export const EnquiryGateContactFields = ({
    firstName,
    lastName,
    onNameChange,
    onNameBlur,
    firstNameRef,
    emailInput,
    phoneInput,
    emailLocked = false,
    errors,
    disabled = false,
    twoColumns,
}: EnquiryGateContactFieldsProps) => (
    <>
        <div className="grid grid-cols-2 gap-1.5">
            <div className="flex flex-col gap-1.5">
                <GateFieldLabel>First Name</GateFieldLabel>
                <GateInput
                    ref={firstNameRef}
                    className={INPUT_CLASS}
                    placeholder="First Name"
                    editHint
                    autoComplete="given-name"
                    disabled={disabled}
                    value={firstName}
                    onChange={(e) => onNameChange(e.target.value, lastName)}
                    onBlur={onNameBlur}
                />
            </div>
            <div className="flex flex-col gap-1.5">
                <GateFieldLabel>Last Name</GateFieldLabel>
                <GateInput
                    className={INPUT_CLASS}
                    placeholder="Last Name"
                    editHint
                    autoComplete="family-name"
                    disabled={disabled}
                    value={lastName}
                    onChange={(e) => onNameChange(firstName, e.target.value)}
                    onBlur={onNameBlur}
                />
            </div>
        </div>
        <GateFieldError message={errors.name} />

        <div
            className={cn(
                'grid gap-1.5',
                twoColumns ? 'grid-cols-2' : 'grid-cols-1',
            )}
        >
            <div className="flex flex-col gap-1.5">
                <GateFieldLabel>Email Address</GateFieldLabel>
                <GateInput
                    {...emailInput}
                    className={cn(INPUT_CLASS, emailLocked && 'opacity-60')}
                    type="email"
                    placeholder="you@email.com"
                    // Not when the address is locked to the account — that is
                    // the one field here that really is read-only, and the
                    // pencil exists to tell the two apart rather than to
                    // decorate every input.
                    editHint={!emailLocked}
                    autoComplete="email"
                    autoCapitalize="off"
                    disabled={disabled || emailLocked}
                />
                <GateFieldError message={errors.email} />
            </div>
            <div className="flex flex-col gap-1.5">
                <GateFieldLabel>Phone</GateFieldLabel>
                <GateInput
                    {...phoneInput}
                    className={INPUT_CLASS}
                    type="tel"
                    placeholder="Phone number"
                    editHint
                    autoComplete="tel"
                    disabled={disabled}
                />
                <GateFieldError message={errors.phoneNumber} />
            </div>
        </div>
    </>
);

export interface EnquiryGateQuestionsProps {
    rules: ConfiguredEnquiryRules;
    answers: ConfiguredEnquiryAnswers;
    errors: ConfiguredEnquiryErrors;
    /** ISO alpha-2 code of where the visitor appears to be. */
    visitorCountry: string | null;
    /** Paired questions share a row. */
    twoColumns: boolean;
    disabled?: boolean;
    /** The location question, first when the form asks it. */
    location?: ReactNode;
    /** The branch question, after the location it may be preselected from. */
    branch?: ReactNode;
    onEnquiryTypeChange: (typeId: string) => void;
    onEnquirerTypeChange: (value: EnquirerType) => void;
    onProjectKindChange: (value: EnquiryProjectKind) => void;
    onFieldAnswerChange: (key: string, value: string) => void;
}

/**
 * The questions a Business Manager form adds between the contact details and
 * the message: location, branch, enquiry type, who they are, project kind and
 * the business's own questions.
 */
export const EnquiryGateQuestions = ({
    rules,
    answers,
    errors,
    visitorCountry,
    twoColumns,
    disabled = false,
    location,
    branch,
    onEnquiryTypeChange,
    onEnquirerTypeChange,
    onProjectKindChange,
    onFieldAnswerChange,
}: EnquiryGateQuestionsProps) => (
    <>
        {location}
        {branch}

        {rules.enquiryTypes.length > 0 && (
            <GateSelectField
                label={FIELD_QUESTIONS.enquiryType}
                placeholder="Select an option"
                value={answers.typeId}
                options={rules.enquiryTypes.map((type) => ({
                    value: String(type.id),
                    label: type.title,
                }))}
                error={errors.enquiryType}
                disabled={disabled}
                onChange={onEnquiryTypeChange}
            />
        )}

        {/* One question per row, like the enquiry type above: their labels are long enough
            to wrap in half a pane, which would set the two boxes at different heights. */}
        {rules.asksEnquirerType && (
            <GateSelectField
                label={FIELD_QUESTIONS.enquirerType}
                placeholder="Select an option"
                value={answers.enquirerType}
                options={ENQUIRER_TYPE_OPTIONS}
                error={errors.enquirerType}
                disabled={disabled}
                onChange={(value) =>
                    onEnquirerTypeChange(value as EnquirerType)
                }
            />
        )}
        {rules.asksProjectKind && (
            <GateSelectField
                label={FIELD_QUESTIONS.projectKind}
                placeholder="Select an option"
                value={answers.projectKind}
                options={PROJECT_KIND_OPTIONS}
                error={errors.projectKind}
                disabled={disabled}
                onChange={(value) =>
                    onProjectKindChange(value as EnquiryProjectKind)
                }
            />
        )}

        {rules.customFields.length > 0 && (
            <EnquiryGateCustomFields
                fields={rules.customFields}
                answers={answers.fieldAnswers}
                errors={errors.fields}
                disabled={disabled}
                visitorCountry={visitorCountry}
                twoColumns={twoColumns}
                onChange={onFieldAnswerChange}
            />
        )}
    </>
);

export interface EnquiryGateMessageFieldProps extends GateTextareaProps {
    required: boolean;
    /** Validation message under the box. */
    error?: string;
}

/** The visitor's own message. */
export const EnquiryGateMessageField = ({
    required,
    error,
    placeholder,
    ...textarea
}: EnquiryGateMessageFieldProps) => (
    <div className="flex flex-col gap-1.5">
        <GateFieldLabel>
            {required ? 'Message' : 'Message (optional)'}
        </GateFieldLabel>
        <GateTextarea
            {...textarea}
            className="h-32.5"
            placeholder={placeholder ?? ENQUIRY_GATE_MESSAGE_HINT}
            autoComplete="off"
        />
        <GateFieldError message={error} />
    </div>
);

export interface EnquiryGateSubmitBarProps {
    /** Shown above the action; empty for none. */
    consentText: string;
    /**
     * A declined answer set has nothing to send, so the action is withdrawn
     * while the consent line stays.
     */
    showSubmit: boolean;
    /** The form's own submit wording, or null for the stock one. */
    label: string | null;
    /** A send is on the wire. */
    isSending?: boolean;
    disabled?: boolean;
}

/** The consent line and the pinned send button. */
export const EnquiryGateSubmitBar = ({
    consentText,
    showSubmit,
    label,
    isSending = false,
    disabled = false,
}: EnquiryGateSubmitBarProps) => (
    <>
        {consentText ? (
            <p className="m-0 text-[0.75rem] leading-relaxed tracking-[-0.02em] text-[#828282]">
                {consentText}
            </p>
        ) : null}

        {/* This form is the one step long enough to overflow either surface,
            so the action is pinned on both — and pinned without a bar behind
            it, the reader's own message scrolled under the button. The footer
            owns the pinning, the device safe area and the spacing beneath; the
            button is just a button.

            `mt-auto` alone. A `pt-*` here is swallowed by the footer's own
            surface padding, which `cn` applies after this class — so it reads
            as clearance that is not there. The form's `gap-5.5` is what
            separates the bar from the last control. */}
        {showSubmit && (
            <GateStickyFooter pinOn="both" className="mt-auto">
                <button
                    type="submit"
                    disabled={disabled}
                    className="flex h-14 w-full items-center justify-center gap-2.5 rounded-[var(--radius-btn-l)] bg-[#181818] text-[1.25rem] font-medium tracking-[-0.02em] text-[#f3f3f3] transition-colors hover:bg-[#2c2c2c] disabled:cursor-not-allowed disabled:opacity-60"
                >
                    {/* The send can take several seconds. Without this the only
                        sign anything happened was the button fading to its
                        disabled colour, which reads as refusal rather than
                        progress. Same words and same spinner as the pane the
                        post-auth resume shows. */}
                    {isSending ? (
                        <>
                            <Loader2 className="size-5 animate-spin" />
                            Sending your enquiry…
                        </>
                    ) : (
                        (label ?? ENQUIRY_GATE_SUBMIT_LABEL)
                    )}
                </button>
            </GateStickyFooter>
        )}
    </>
);
